<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-yellow" sprite="ambulance" />
          Parking Lots
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-yellow" sprite="ambulance" />
            Parking Lots
          </h1>
        </el-col>

        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px"></i>
            Total: {{ totalRecords }}
          </el-tag>
        </el-col>
      </el-row>
      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable />
          <el-button class="ml-10" @click="doExport">Export</el-button>
        </div>
        <div v-if="tableData.length && maxPage > 1" class="box right">
          <el-pagination
            small
            background
            layout="prev, pager, next"
            @current-change="pageSet"
            @next-click="pageNext"
            @prev-click="pagePrevious"
            :current-page="page"
            :page-size="recordsPerPage"
            :pager-count="5"
            :total="totalRecords"
          />
        </div>
      </div>
      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          stripe
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>

    <edit-form
      :clearForm="clearForm"
      :editVisible="editVisible"
      :editProcessing="editProcessing"
      :selectedParkinglot="selectedParkinglot"
      @editOperation="editOperation"
      @cancelOperation="cancelOperation"
    />
  </div>
</template>

<script>
import { debounce } from "lodash";
import { ExportToCsv } from "export-to-csv";
import EditForm from "./components/editForm.vue";
import { apiV2Host } from "../../../feathers/feathers-client";

export default {
  name: "ParkinglotsTable",

  components: {
    EditForm,
  },

  data() {
    return {
      search: "",
      totalRecords: 0,
      recordsPerPage: 100,
      page: 1,
      maxPage: 1,
      tableLoading: false,
      tableData: [],
      allParkinglots: [],
      visibleActionsId: "",
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: ({ row }) => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-yellow" sprite="ambulance" />
                </div>,
              ];
            },
          },
        },
        {
          field: "state",
          title: "",
          width: 45,
          slots: {
            default: ({ row }) => {
              return [
                <status-indicator
                  pulse={false}
                  status={row.state === 0 ? "negative" : "positive"}
                />,
              ];
            },
          },
        },
        {
          field: "space",
          title: "Lot",
          sortable: true,
          sortType: "number",
          minWidth: 50,
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return row.space.split("-")[0];
            },
          },
        },
        {
          field: "timeoutType",
          title: "Timeout Type",
          sortable: true,
          minWidth: 300,
          showOverflow: true,
          slots: {
            default: ({row}) => {
              return this.getDestinationRowLabel(row.timeoutType);
            }
          }
        },
        {
          field: "alternateRing",
          title: "Alternate Ring",
          sortable: true,
          minWidth: 100,
          showOverflow: true,
          slots: {
            default: ({row}) => {
              return row.useAlternateRing ? "Yes" : "No";
            }
          }
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="el-icon-edit"
                    onClick={() => this.openEditModal(row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    v-show={this.canClearParkingLots && !(row.state === 0 && !row.hostIp)}
                    id={"delete-" + row.id}
                    icon="el-icon-delete"
                    onClick={() => this.clearParkingLot(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>,
              ];
            },
          },
        },
      ],
      clearForm: false,
      editVisible: false,
      editProcessing: false,
      selectedParkinglot: null,
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    },
    canClearParkingLots() {
      return this.$store.state.auth.user.userRole === 1 || this.$store.state.auth.user.userRole === 11;
    }
  },

  watch: {
    selectedTenant() {
      this.findParkinglots();
    },

    user() {
      this.findParkinglots();
    },

    page: debounce(function () {
      this.findParkinglots();
    }, 500),

    search: debounce(function () {
      if (this.search) {
        this.tableData = this.allParkinglots.filter((p) =>
          p.space.includes(this.search)
        );
        return;
      }

      this.tableData = [...this.allParkinglots];
    }, 500),
  },

  methods: {
    pageNext() {
      if (this.page < this.maxPage) {
        this.page += 1;
      }
    },

    pagePrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },

    pageSet(page) {
      this.page = page;
    },

    //General Form and Table Operations
    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    doExport() {
      const data = [...this.tableData];
      if (data.length) {
        const writeData = data.map((item) => ({
          lot: item.space.split("-")[0],
          state: item.state === 0 ? "Empty" : "Full",
          timeoutType: item.timeoutType,
        }));

        const options = {
          filename: "parkinglots",
          showLabels: true,
          headers: ["Lot", "State", "Timeout Type"],
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(writeData);
      }
    },

    async findParkinglots() {
      this.tableLoading = true;
      this.tableData = [];

      try {
        let url = `${apiV2Host}/admin/parking-sessions?tenantId=${this.selectedTenant.id}&recordsPerPage=${this.recordsPerPage}&page=${this.page}`;

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
          },
        });

        const data = await res.json();

        const { records, totalRecords, maxPage } = data;

        this.tableData = [...records];
        this.allParkinglots = [...records];
        this.totalRecords = totalRecords;
        this.maxPage = maxPage;
        this.tableLoading = false;
      } catch (err) {
        this.tableData = [];
        this.tableLoading = false;
      }
    },

    getDestinationRowLabel(dst) {
      const labelMap  = {
        SPECIAL: "Automation",
        CUSTOM: "Custom",
        IVR: "Go to a virtual receptionist",
        VOICEMAIL: "Go to a voicemail",
        PLAYBACK: "Play an audio file",
        HUNTLIST: "Ring a call group",
        EXT: "Ring an extension",
        QUEUE: "Ring a queue",
        SMS: "Send a text message",
        MUSICONHOLD: "Set hold music",
        CONDITION: "Time of day",
        MEETME: "Go to a conference room",
        DISABLEUNCONDITIONALDID: "Disable forwarding for number",
        ENABLEUNCONDITIONALDID: "Enable forwarding for number",
        LOGINQUEUE: "Login to queue",
        LOGOUTQUEUE: "Log out of queue",
        RERECORD: "Rerecord",
        SETUNCONDITIONALDID: "Set forwarding for number",
        TOGGLEPAUSEQUEUE: "Toggle queue pause",
        TOGGLEUNCONDITIONALDID: "Toggle forwarding for number",
        DISABLEUNCONDITIONALHUNTLIST: "Disable forwarding for call group",
        ENABLEUNCONDITIONALHUNTLIST: "Enable forwarding for call group",
        TOGGLEUNCONDITIONALHUNTLIST: "Toggle forwarding for call group",
        SETUNCONDITIONALHUNTLIST: "Set forwarding for call group",
        PIONEER_OUTBOUND_REFILL_AGENT: "Pioneer Outbound Refill Agent (EN)",
        PIONEER_AGENT: "Pioneer IVR (EN)",
        PIONEER_REFILL: "PioneerRx Refill (EN)",
        PIONEER_STATUS: "PioneerRx Status (EN)",
        PIONEER_SPANISH_AGENT: "Pioneer IVR (ES)",
        PIONEER_SPANISH_REFILL: "PioneerRx Refill (ES)",
        PIONEER_SPANISH_STATUS: "PioneerRx Status (ES)",
        PIONEER_IVA: "Pioneer Agent",
        BEST_RX_IVA: "BestRx Agent",
        BEST_RX_REFILL: "BestRx Refill (EN)",
        BEST_RX_STATUS: "BestRx Status (EN)",
        BEST_RX_SPANISH_REFILL: "BestRx Refill (ES)",
        BEST_RX_SPANISH_STATUS: "BestRx Status (ES)",
        LIBERTY_RX_IVA: "LibertyRx Agent",
        LIBERTY_RX_REFILL: "LibertyRx Refill (EN)",
        LIBERTY_RX_STATUS: "LibertyRx Status (EN)",
        LIBERTY_RX_SPANISH_REFILL: "LibertyRx Refill (ES)",
        LIBERTY_RX_SPANISH_STATUS: "LibertyRx Status (ES)"
      }

      return labelMap[dst] || dst;
    },

    openEditModal(id) {
      this.tableLoading = true;
      this.clearForm = false;

      fetch(`${apiV2Host}/admin/parking-sessions/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
          Accept: "application/json",
        },
      })
        .then((res) => res.json())
        .then((record) => {
          this.tableLoading = false;
          this.editVisible = true;
          this.selectedParkinglot = record;
        })
        .catch((err) => {
          this.tableLoading = false;
        });
    },

    editOperation(editBody) {
      this.$confirm("Edit Parking Lot?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(async () => {
          try {
            this.editProcessing = true;

            const res = await fetch(
              `${apiV2Host}/admin/parking-sessions/${this.selectedParkinglot.id}`,
              {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
                  Accept: "application/json",
                },
                body: JSON.stringify(editBody),
              }
            );

            const response = await res.json();

            this.editProcessing = false;

            if (!res.ok) {
              const { name, message, validationErrors } = response;
              this.$notify({
                title: name,
                message:
                  validationErrors && validationErrors.length
                    ? validationErrors[0]
                    : message,
                type: "error",
              });
              return;
            }

            this.findParkinglots();

            this.clearForm = true;
            this.cancelOperation();

            this.$notify({
              type: "success",
              title: "Success",
              message: "Edited successfully",
            });
          } catch (err) {
            console.dir(err);
            this.editProcessing = false;
          }
        })
        .catch(() => true);
    },

    cancelOperation() {
      this.clearForm = true;
      this.editVisible = false;
      this.selectedParkinglot = null;
    },

    async clearParkingLot(id) {

      this.$confirm("Clear Parking Lot?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      }).then(async () => {

        try {
          const res = await fetch(
            `${apiV2Host}/admin/parking-sessions/clear/${id}`,
            {
              method: "PATCH",
              headers: {
                Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
                Accept: "application/json",
              }
            }
          );

          if (!res.ok) {
            const response = await res.json();
            const { name, message, validationErrors } = response;
            this.$notify({
              title: name,
              message:
                validationErrors && validationErrors.length
                  ? validationErrors[0]
                  : message,
              type: "error",
            });
            return;
          }

          this.findParkinglots();

          this.$notify({
            type: "success",
            title: "Success",
            message: "Cleared successfully",
          });
        } catch (e) {
          console.dir(e);
        }


      }).catch(() => true);

    }
  },

  mounted() {
    if (this.user) {
      this.findParkinglots();
    }
  },
};
</script>
